/**
 * Performance Hub — XV Vision (AI Match Analysis) + GPS / Wearables.
 * Single page accessible from Player / Coach / School / Admin dashboards.
 *
 * Tabs: XV Vision (match analysis) · GPS Sessions (training load) · ACWR
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import {
  Video, Upload, Activity, Target, Zap, Heart, Gauge, ArrowLeft,
  Trophy, Trash2, Loader2, AlertCircle, FileUp, Calendar, MapPin,
  Watch, Link2, Sparkles,
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import XVAssistWidget from '@/components/XVAssistWidget';

function Pill({ children, color }) {
  return <span className={`text-[10px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 ${color || 'text-zinc-400 border-zinc-700'}`}>{children}</span>;
}

function VisionPanel() {
  const [matches, setMatches] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    source_url: '', match_date: '', opponent: '', competition: '', own_team_name: '', location: '', notes: '',
  });
  const [videoFile, setVideoFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [m, a] = await Promise.all([
        api.get('/xv-vision/matches'),
        api.get('/xv-vision/aggregate'),
      ]);
      setMatches(m.data);
      setAggregate(a.data);
      return m.data;
    } catch {
      toast.error('Failed to load XV Vision matches');
      return [];
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Poll while any match is processing
  useEffect(() => {
    if (!matches.some((m) => m.status === 'processing')) return undefined;
    const t = setInterval(async () => {
      const fresh = await refresh();
      const done = fresh.filter((m) => m.status === 'analyzed');
      if (!fresh.some((m) => m.status === 'processing') && done.length) {
        toast.success('AI match analysis complete');
      }
    }, 6000);
    return () => clearInterval(t);
  }, [matches, refresh]);

  const CHUNK = 2 * 1024 * 1024;

  const uploadVideoChunked = async () => {
    const init = await api.post('/xv-vision/upload-video/init');
    const uploadId = init.data.upload_id;
    const total = Math.ceil(videoFile.size / CHUNK);
    for (let i = 0; i < total; i += 1) {
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('file', videoFile.slice(i * CHUNK, (i + 1) * CHUNK), `chunk_${i}`);
      // eslint-disable-next-line no-await-in-loop
      await api.post('/xv-vision/upload-video/chunk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadPct(Math.round(((i + 1) / total) * 100));
    }
    const payload = {
      upload_id: uploadId,
      filename: videoFile.name,
      match_date: form.match_date,
      opponent: form.opponent,
      own_team_name: form.own_team_name,
    };
    if (form.competition) payload.competition = form.competition;
    if (form.location) payload.location = form.location;
    if (form.notes) payload.notes = form.notes;
    await api.post('/xv-vision/upload-video/complete', payload);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.match_date || !form.opponent || !form.own_team_name) {
      toast.error('Date, opponent and own team are required');
      return;
    }
    if (!videoFile && !form.source_url) {
      toast.error('Attach a match video file for AI analysis, or paste a video URL to save a link reference');
      return;
    }
    setSubmitting(true);
    try {
      if (videoFile) {
        setUploadPct(0);
        await uploadVideoChunked();
        toast.success('Video uploaded — AI frame analysis is running. This takes a minute or two.');
      } else {
        const payload = { ...form };
        if (!payload.competition) delete payload.competition;
        if (!payload.location) delete payload.location;
        if (!payload.notes) delete payload.notes;
        await api.post('/xv-vision/upload', payload);
        toast.success('Match link saved. Upload the video file for real AI analysis.');
      }
      setForm({ source_url: '', match_date: '', opponent: '', competition: '', own_team_name: '', location: '', notes: '' });
      setVideoFile(null);
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Upload failed');
    } finally { setSubmitting(false); setUploadPct(null); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this match analysis?')) return;
    try { await api.delete(`/xv-vision/matches/${id}`); refresh(); } catch { toast.error('Delete failed'); }
  };

  const onPickVideo = (f) => {
    if (!f) { setVideoFile(null); return; }
    const ok = ['.mp4', '.mov', '.m4v', '.webm'].some((ext) => f.name.toLowerCase().endsWith(ext));
    if (!ok) { toast.error('Use MP4, MOV, M4V or WEBM video files'); return; }
    if (f.size > 200 * 1024 * 1024) { toast.error('Video exceeds the 200 MB limit'); return; }
    setVideoFile(f);
  };

  const statusBadge = (m) => {
    if (m.status === 'processing') return <Badge className="bg-blue-500/10 text-blue-300 border-blue-500/30"><Loader2 className="w-3 h-3 mr-1 animate-spin inline" />AI analysing…</Badge>;
    if (m.status === 'failed') return <Badge className="bg-red-500/10 text-red-300 border-red-500/30">Analysis failed</Badge>;
    if (m.status === 'link_only') return <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700">Link only — no AI analysis</Badge>;
    if (m.analysis) {
      const r = m.analysis.score?.result || 'Unknown';
      return (
        <>
          <Badge className={r === 'Win' ? 'bg-[#A3FF00]/10 text-[#A3FF00] border-[#A3FF00]/30' : r === 'Loss' ? 'bg-red-500/10 text-red-300 border-red-500/30' : 'bg-zinc-800 text-zinc-300 border-zinc-700'}>
            {r}{r !== 'Unknown' ? ` ${m.analysis.score.home}-${m.analysis.score.away}` : ''}
          </Badge>
          <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px]">AI {m.analysis.ai_confidence_pct}%</Badge>
        </>
      );
    }
    return null;
  };

  if (loading) return <Card className="bg-zinc-900 border-zinc-800 p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></Card>;

  return (
    <div className="space-y-6">
      {aggregate && aggregate.matches_analysed > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="vision-aggregate">
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Matches</p><p className="text-2xl font-bold text-[#A3FF00]">{aggregate.matches_analysed}</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Wins</p><p className="text-2xl font-bold text-white">{aggregate.wins}</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Tries</p><p className="text-2xl font-bold text-white">{aggregate.tries}</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Tackle Success</p><p className="text-2xl font-bold text-white">{aggregate.avg_tackle_success_pct}%</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Possession</p><p className="text-2xl font-bold text-white">{aggregate.avg_possession_pct}%</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Form (last 5)</p><p className="text-lg font-mono text-[#A3FF00]">{aggregate.form_last_5.join(' · ') || '—'}</p></Card>
        </div>
      )}

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><Video className="w-5 h-5 text-[#A3FF00]" />Submit a Match for AI Analysis</h3>
          <Pill color="text-[#A3FF00] border-[#A3FF00]/40">XV Vision AI v2.0</Pill>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vision-upload-form">
          <div className="md:col-span-2">
            <Label className="text-zinc-400 text-xs">Match Video File (MP4/MOV, max 200 MB) — required for real AI analysis</Label>
            <div className="flex items-center gap-3 mt-1">
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
                onChange={(e) => onPickVideo(e.target.files?.[0])}
                className="block w-full text-sm text-zinc-400 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#A3FF00] file:text-black file:font-bold file:text-xs hover:file:bg-[#88dd00] bg-zinc-950 border border-zinc-800 rounded"
                data-testid="vision-video-file-input"
              />
              {videoFile && <Badge className="bg-[#A3FF00]/10 text-[#A3FF00] border-[#A3FF00]/30 shrink-0">{(videoFile.size / (1024 * 1024)).toFixed(1)} MB</Badge>}
            </div>
            {uploadPct !== null && (
              <div className="mt-2" data-testid="vision-upload-progress">
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-[#A3FF00] transition-all" style={{ width: `${uploadPct}%` }} /></div>
                <p className="text-zinc-500 text-[11px] mt-1">Uploading… {uploadPct}%</p>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-400 text-xs">Or a Video URL (YouTube, Veo, Hudl, Vimeo) — saved as a link reference only</Label>
            <Input data-testid="vision-source-url" value={form.source_url} onChange={(e) => setForm({...form, source_url: e.target.value})} placeholder="https://youtu.be/..." className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Match Date *</Label>
            <Input type="date" data-testid="vision-match-date" required value={form.match_date} onChange={(e) => setForm({...form, match_date: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Competition</Label>
            <Input data-testid="vision-competition" value={form.competition} onChange={(e) => setForm({...form, competition: e.target.value})} placeholder="Craven Week, League, Trial..." className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Own Team Name *</Label>
            <Input data-testid="vision-own-team" required value={form.own_team_name} onChange={(e) => setForm({...form, own_team_name: e.target.value})} placeholder="Strandfontein High 1st XV" className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Opponent *</Label>
            <Input data-testid="vision-opponent" required value={form.opponent} onChange={(e) => setForm({...form, opponent: e.target.value})} placeholder="Grey College" className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Location / Venue</Label>
            <Input data-testid="vision-location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Newlands, Cape Town" className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Coach Notes (optional)</Label>
            <Input data-testid="vision-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Focus area for review..." className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" disabled={submitting} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold" data-testid="vision-submit-button">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}{videoFile ? 'Upload & Analyse Match' : 'Save Match'}
            </Button>
          </div>
        </form>
        <p className="text-zinc-500 text-[11px] mt-3">XV Vision AI v2.0 samples frames from your match video and runs real GPT-4o computer-vision analysis: phases of play, contact events, set piece, scoreboard reads and coaching observations.</p>
      </Card>

      {/* MATCH LIST */}
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-heading font-bold text-white mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#A3FF00]" />Match Library</h3>
        {matches.length === 0 ? (
          <p className="text-zinc-500 text-sm">No matches analysed yet. Submit your first one above.</p>
        ) : (
          <div className="space-y-2">
            {matches.map((m) => (
              <div key={m.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 hover:border-[#A3FF00]/40 transition-colors" data-testid={`match-row-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-white">{m.own_team_name} vs {m.opponent}</span>
                      {statusBadge(m)}
                    </div>
                    <p className="text-zinc-500 text-xs mt-1">{m.match_date} · {m.competition || 'Friendly'} · {m.location || '—'}</p>
                    {m.status === 'failed' && m.analysis_error && <p className="text-red-400 text-[11px] mt-1">{m.analysis_error}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {m.analysis && <Button size="sm" variant="outline" onClick={() => setSelected(m)} className="border-zinc-700 text-zinc-300 hover:text-[#A3FF00]" data-testid={`view-match-${m.id}`}>View</Button>}
                    {m.status === 'link_only' && m.source_url && (
                      <a href={m.source_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300"><Link2 className="w-3.5 h-3.5" /></Button></a>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => onDelete(m.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-match-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {selected && <MatchDetail match={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function MatchDetail({ match, onClose }) {
  const [commentary, setCommentary] = useState(match.ai_commentary || null);
  const [loading, setLoading] = useState(false);
  const fetchCommentary = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/xv-vision/matches/${match.id}/commentary`);
      setCommentary(res.data.commentary);
    } catch { toast.error('Auto-commentary failed'); }
    finally { setLoading(false); }
  };
  return (
    <Card className="bg-zinc-900 border-[#A3FF00]/40 p-5" data-testid="match-detail-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-white">Analysis · {match.own_team_name} vs {match.opponent}</h3>
        <Button size="sm" variant="ghost" onClick={onClose} className="text-zinc-400">Close</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(match.analysis.team_stats).map(([k, v]) => (
          <div key={k} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider">{k.replace(/_/g, ' ')}</p>
            <p className="text-xl font-bold text-white">{typeof v === 'number' && (k.includes('pct') || k.includes('success')) ? `${v}%` : v}</p>
          </div>
        ))}
      </div>
      <h4 className="font-heading font-bold text-white mt-5 mb-2">Highlights ({match.analysis.highlights.length})</h4>
      <div className="space-y-1.5">
        {match.analysis.highlights.map((h, i) => (
          <div key={i} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded p-2.5 text-sm">
            <span className="text-zinc-300">{Math.floor(h.timestamp_s / 60)}:{String(h.timestamp_s % 60).padStart(2, '0')} — {h.type}</span>
            <Badge className="bg-[#A3FF00]/10 text-[#A3FF00] border-[#A3FF00]/30 text-[10px]">Confidence {Math.round(h.confidence * 100)}%</Badge>
          </div>
        ))}
      </div>

      {match.analysis.observations && match.analysis.observations.length > 0 && (
        <div className="mt-5">
          <h4 className="font-heading font-bold text-white mb-2">AI Observations</h4>
          <ul className="space-y-1.5" data-testid="match-observations">
            {match.analysis.observations.map((o, i) => (
              <li key={i} className="text-zinc-300 text-sm bg-zinc-950 border border-zinc-800 rounded p-2.5">{o}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-zinc-800">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-heading font-bold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#A3FF00]" />XV Assist Coach Commentary</h4>
          {!commentary && (
            <Button size="sm" onClick={fetchCommentary} disabled={loading} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold" data-testid="generate-commentary-button">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              Generate
            </Button>
          )}
        </div>
        {commentary ? (
          <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid="match-commentary">{commentary}</p>
        ) : !loading && (
          <p className="text-zinc-500 text-sm">Click Generate to get tactical commentary on this match from XV Assist.</p>
        )}
      </div>

      <p className="text-zinc-500 text-[11px] mt-4">{match.analysis.engine_version}</p>
    </Card>
  );
}

function GpsPanel() {
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    session_date: '', duration_min: '', distance_m: '', high_speed_running_m: '',
    sprints: '', max_velocity_ms: '', avg_heart_rate: '', rpe: '', session_type: 'training', notes: '',
  });

  const refresh = useCallback(async () => {
    try {
      const [s, sum] = await Promise.all([api.get('/gps/sessions'), api.get('/gps/summary')]);
      setSessions(s.data);
      setSummary(sum.data);
    } catch {
      toast.error('Failed to load GPS sessions');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        session_date: form.session_date,
        duration_min: parseFloat(form.duration_min),
        distance_m: parseFloat(form.distance_m),
        high_speed_running_m: parseFloat(form.high_speed_running_m || 0),
        sprints: parseInt(form.sprints || 0, 10),
        max_velocity_ms: parseFloat(form.max_velocity_ms || 0),
        session_type: form.session_type,
      };
      if (form.avg_heart_rate) payload.avg_heart_rate = parseInt(form.avg_heart_rate, 10);
      if (form.rpe) payload.rpe = parseInt(form.rpe, 10);
      if (form.notes) payload.notes = form.notes;
      await api.post('/gps/sessions', payload);
      toast.success('GPS session logged.');
      setForm({ session_date: '', duration_min: '', distance_m: '', high_speed_running_m: '', sprints: '', max_velocity_ms: '', avg_heart_rate: '', rpe: '', session_type: 'training', notes: '' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log session');
    } finally { setSubmitting(false); }
  };

  const uploadGpx = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('session_type', 'training');
    setSubmitting(true);
    try {
      await api.post('/gps/upload-gpx', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('GPX file imported.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'GPX upload failed');
    } finally { setSubmitting(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try { await api.delete(`/gps/sessions/${id}`); refresh(); } catch { toast.error('Delete failed'); }
  };

  if (loading) return <Card className="bg-zinc-900 border-zinc-800 p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></Card>;

  const acwrColor = summary?.acwr_band === 'Optimal' ? 'text-[#A3FF00] border-[#A3FF00]/30' :
                    summary?.acwr_band === 'Monitor' ? 'text-yellow-400 border-yellow-500/30' :
                    summary?.acwr_band === 'High Risk' ? 'text-red-400 border-red-500/30' :
                    'text-zinc-400 border-zinc-700';

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="gps-summary">
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Sessions</p><p className="text-2xl font-bold text-[#A3FF00]">{summary.total_sessions}</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Total Distance</p><p className="text-2xl font-bold text-white">{summary.total_distance_km} km</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">HSR Total</p><p className="text-2xl font-bold text-white">{summary.total_hsr_km} km</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Sprints</p><p className="text-2xl font-bold text-white">{summary.total_sprints}</p></Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4"><p className="text-zinc-400 text-[10px] uppercase">Max Velocity</p><p className="text-2xl font-bold text-white">{summary.max_velocity_ms} m/s</p></Card>
          <Card className={`bg-zinc-900 border ${acwrColor} p-4`} data-testid="gps-acwr">
            <p className="text-zinc-400 text-[10px] uppercase">ACWR</p>
            <p className="text-2xl font-bold">{summary.acwr || '—'}</p>
            <p className="text-[10px] mt-0.5">{summary.acwr_band}</p>
          </Card>
        </div>
      )}

      {summary && summary.load_trend_28d.length > 0 && summary.total_sessions > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white flex items-center gap-2"><Activity className="w-5 h-5 text-[#A3FF00]" />28-Day Training Load</h3>
            <Pill color={acwrColor}>{summary.acwr_band}</Pill>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary.load_trend_28d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={9} tickFormatter={(d) => d.slice(5)} interval={2} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
              <Bar dataKey="load" fill="#A3FF00" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3 flex items-center gap-2"><FileUp className="w-5 h-5 text-[#A3FF00]" />Import GPX File</h3>
          <p className="text-zinc-400 text-xs mb-3">From Garmin, Strava, Suunto, Polar — drop a .gpx file to auto-import distance, duration and load.</p>
          <label className="cursor-pointer block">
            <input
              type="file"
              accept=".gpx"
              onChange={(e) => uploadGpx(e.target.files?.[0])}
              className="hidden"
              data-testid="gpx-file-input"
            />
            <div className="border-2 border-dashed border-zinc-700 hover:border-[#A3FF00]/40 rounded-lg p-8 text-center transition-colors">
              <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
              <p className="text-zinc-300 text-sm">{submitting ? 'Importing…' : 'Click to choose a .gpx file'}</p>
              <p className="text-zinc-600 text-xs mt-1">Max 5 MB</p>
            </div>
          </label>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3 flex items-center gap-2"><Gauge className="w-5 h-5 text-[#A3FF00]" />Log Session Manually</h3>
          <form onSubmit={submit} className="grid grid-cols-2 gap-2" data-testid="gps-manual-form">
            <div className="col-span-2"><Label className="text-zinc-400 text-xs">Date *</Label>
              <Input type="date" required value={form.session_date} onChange={(e) => setForm({...form, session_date: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" data-testid="gps-date" /></div>
            <div><Label className="text-zinc-400 text-xs">Duration (min) *</Label>
              <Input type="number" required value={form.duration_min} onChange={(e) => setForm({...form, duration_min: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" data-testid="gps-duration" /></div>
            <div><Label className="text-zinc-400 text-xs">Distance (m) *</Label>
              <Input type="number" required value={form.distance_m} onChange={(e) => setForm({...form, distance_m: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" data-testid="gps-distance" /></div>
            <div><Label className="text-zinc-400 text-xs">HSR (m)</Label>
              <Input type="number" value={form.high_speed_running_m} onChange={(e) => setForm({...form, high_speed_running_m: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" /></div>
            <div><Label className="text-zinc-400 text-xs">Sprints</Label>
              <Input type="number" value={form.sprints} onChange={(e) => setForm({...form, sprints: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" /></div>
            <div><Label className="text-zinc-400 text-xs">Max Velocity (m/s)</Label>
              <Input type="number" step="0.1" value={form.max_velocity_ms} onChange={(e) => setForm({...form, max_velocity_ms: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" /></div>
            <div><Label className="text-zinc-400 text-xs">Avg HR (bpm)</Label>
              <Input type="number" value={form.avg_heart_rate} onChange={(e) => setForm({...form, avg_heart_rate: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" /></div>
            <div className="col-span-2"><Label className="text-zinc-400 text-xs">RPE (1-10)</Label>
              <Input type="number" min="1" max="10" value={form.rpe} onChange={(e) => setForm({...form, rpe: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white" /></div>
            <Button type="submit" disabled={submitting} className="col-span-2 bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold" data-testid="gps-submit-button">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}Log Session
            </Button>
          </form>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-heading font-bold text-white mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-[#A3FF00]" />Session History ({sessions.length})</h3>
        {sessions.length === 0 ? (
          <p className="text-zinc-500 text-sm">No sessions logged yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="gps-sessions-table">
              <thead className="text-zinc-500 text-[10px] uppercase">
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-2">Date</th><th className="text-left py-2">Type</th><th className="text-left py-2">Duration</th>
                  <th className="text-left py-2">Distance</th><th className="text-left py-2">HSR</th><th className="text-left py-2">Sprints</th>
                  <th className="text-left py-2">Max V</th><th className="text-left py-2">HR</th><th className="text-left py-2">Load</th><th className="text-left py-2">Source</th><th></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-2 text-white">{s.session_date}</td>
                    <td className="py-2 text-zinc-400 capitalize">{s.session_type}</td>
                    <td className="py-2 text-zinc-300 font-mono">{s.duration_min} min</td>
                    <td className="py-2 text-zinc-300 font-mono">{(s.distance_m / 1000).toFixed(2)} km</td>
                    <td className="py-2 text-zinc-300 font-mono">{(s.high_speed_running_m / 1000).toFixed(2)} km</td>
                    <td className="py-2 text-zinc-300 font-mono">{s.sprints}</td>
                    <td className="py-2 text-zinc-300 font-mono">{s.max_velocity_ms}</td>
                    <td className="py-2 text-zinc-300 font-mono">{s.avg_heart_rate || '—'}</td>
                    <td className="py-2"><span className="text-[#A3FF00] font-mono">{s.training_load}</span></td>
                    <td className="py-2"><Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px]">{s.source}</Badge></td>
                    <td className="py-2"><Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-7" onClick={() => onDelete(s.id)}><Trash2 className="w-3 h-3" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function WearablesPanel() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try { setProviders((await api.get('/wearables/providers')).data); }
    catch { toast.error('Failed to load wearables'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const connect = async (key) => {
    try {
      const res = await api.get(`/wearables/connect/${key}`);
      toast.success(res.data?.message || 'Ready');
      refresh();
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Connect failed';
      toast.info(detail, { duration: 8000 });
    }
  };

  const disconnect = async (key) => {
    if (!window.confirm('Disconnect this wearable?')) return;
    try { await api.post(`/wearables/disconnect/${key}`); refresh(); }
    catch { toast.error('Disconnect failed'); }
  };

  if (loading) return <Card className="bg-zinc-900 border-zinc-800 p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></Card>;
  return (
    <div className="space-y-4" data-testid="wearables-panel">
      {providers.map((p) => (
        <Card key={p.key} className="bg-zinc-900 border-zinc-800 p-5" data-testid={`wearable-${p.key}`}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-center shrink-0">
              <Watch className="w-6 h-6 text-[#A3FF00]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-bold text-white">{p.label}</h3>
                {p.user_connected ? (
                  <Badge className="bg-[#A3FF00]/10 text-[#A3FF00] border-[#A3FF00]/30 text-[10px]">CONNECTED</Badge>
                ) : p.configured_on_platform ? (
                  <Badge className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px]">READY</Badge>
                ) : (
                  <Badge className="bg-yellow-500/10 text-yellow-300 border-yellow-500/30 text-[10px]">REQUIRES SETUP</Badge>
                )}
              </div>
              <p className="text-zinc-500 text-xs mt-1">{p.method}</p>
              {!p.configured_on_platform && (
                <p className="text-zinc-600 text-[11px] mt-1">
                  Platform admin must set <code className="text-zinc-300">{p.required_env_vars.join(', ')}</code> · <a className="text-[#A3FF00] hover:underline" href={p.developer_url} target="_blank" rel="noopener noreferrer">Developer portal →</a>
                </p>
              )}
            </div>
            {p.user_connected ? (
              <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10" onClick={() => disconnect(p.key)} data-testid={`wearable-disconnect-${p.key}`}>Disconnect</Button>
            ) : (
              <Button size="sm" className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold" onClick={() => connect(p.key)} data-testid={`wearable-connect-${p.key}`}>
                <Link2 className="w-3.5 h-3.5 mr-1" />Connect
              </Button>
            )}
          </div>
        </Card>
      ))}
      <Card className="bg-zinc-900 border-zinc-800 border-dashed p-4 text-center">
        <p className="text-zinc-500 text-xs">No wearable? Use the manual log or GPX upload on the GPS tab. Apple Health requires the FutureXV iOS companion app (Phase 2).</p>
      </Card>
    </div>
  );
}

export default function PerformanceHub() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="performance-hub">
      <nav className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate(-1)} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="back-button">
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-600">|</span>
              <span className="font-heading font-bold">Performance Hub</span>
            </div>
            <span className="text-zinc-500 text-sm">{user?.full_name}</span>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-4xl font-heading font-bold mb-1">Performance Hub</h1>
          <p className="text-zinc-400 text-sm">AI-powered match analysis (XV Vision) plus your GPS / wearable training data.</p>
        </div>

        <Tabs defaultValue="vision" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6 bg-zinc-900 max-w-2xl" data-testid="performance-hub-tabs">
            <TabsTrigger value="vision" className="data-[state=active]:bg-[#A3FF00] data-[state=active]:text-black" data-testid="tab-xv-vision"><Video className="w-4 h-4 mr-2" />XV Vision</TabsTrigger>
            <TabsTrigger value="gps" className="data-[state=active]:bg-[#A3FF00] data-[state=active]:text-black" data-testid="tab-gps"><Activity className="w-4 h-4 mr-2" />GPS &amp; Wearables</TabsTrigger>
            <TabsTrigger value="connect" className="data-[state=active]:bg-[#A3FF00] data-[state=active]:text-black" data-testid="tab-wearables"><Watch className="w-4 h-4 mr-2" />Connect Devices</TabsTrigger>
          </TabsList>
          <TabsContent value="vision"><VisionPanel /></TabsContent>
          <TabsContent value="gps"><GpsPanel /></TabsContent>
          <TabsContent value="connect"><WearablesPanel /></TabsContent>
        </Tabs>
      </div>

      <XVAssistWidget />
    </div>
  );
}
